import { useCallback } from 'react';
import { useGetPlanCommand } from '@atlassian/jira-command-palette-common/src/common/ui/command/plan/index.tsx';
import type { Command } from '@atlassian/jira-command-palette/src/common/types/commands/index.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { PLAN_LIMIT } from '../constants.tsx';
import { messages } from '../messages.tsx';
import type { GetQuickSearchChildResultProps } from '../types.tsx';
import { getQuickSearchPlanScope, transformQuickSearchPlanResultToPlan } from '../utils.tsx';
import { useGetViewAllCommand } from '../view-all/index.tsx';
import { ViewAllType, getViewAllUrl } from '../view-all/utils.tsx';
import type {
	UseGetQuickSearchPlanCommandsProps,
	UseGetQuickSearchPlanCommandsResponse,
} from './types.tsx';

export const useGetQuickSearchPlanCommands = ({
	section,
	showViewAll,
}: UseGetQuickSearchPlanCommandsProps): UseGetQuickSearchPlanCommandsResponse => {
	const { formatMessage } = useIntl();

	const { getPlanCommand } = useGetPlanCommand();
	const { getViewAllCommand } = useGetViewAllCommand();

	const getQuickSearchPlanCommands = useCallback(
		({ search, quickSearchResponseData, error }: GetQuickSearchChildResultProps) => {
			const planScope = getQuickSearchPlanScope(quickSearchResponseData);

			// No results
			if (!planScope || error || planScope?.error || planScope?.results?.length === 0) {
				return [];
			}

			const commands: Command[] = planScope.results.slice(0, PLAN_LIMIT).map((plan) =>
				getPlanCommand({
					plan: transformQuickSearchPlanResultToPlan(plan),
					keywords: search,
					section,
					analytics: {
						isSearchResult: true,
					},
				}),
			);

			if (!showViewAll || planScope.results.length <= PLAN_LIMIT) {
				return commands;
			}

			const viewAllCommand = getViewAllCommand({
				id: 'view-all-plans',
				keywords: search,
				section,
				name: formatMessage(messages.viewAllResults),
				// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
				url: getViewAllUrl(ViewAllType.PLAN, search)!,
				analyticsPayload: { action: 'viewAllPlans', size: planScope.size },
			});

			return commands.concat([viewAllCommand]);
		},
		[formatMessage, getPlanCommand, getViewAllCommand, section, showViewAll],
	);

	return { getQuickSearchPlanCommands };
};
